/**
 * "Edit sales hierarchy" on the event page (RM/TM role setup 2026-10-29, REQ-8.3; fixes ISS-8.1 and ISS-8.2).
 *
 * Replaces the inline Sales Rep dropdown in the Event Details grid, which
 *   - changed only the Lead/Event Rep: the Territory Manager, Regional Manager and Sales Director stayed as they
 *     were (e.g. on a duplicated class), and
 *   - put Save / Cancel where the next grid column covered them, so they could not be clicked.
 * The dialog uses the same hierarchy box as the create wizard (SalesHierarchyFields): picking a new Lead refills the
 * manager levels from that rep's reporting chain (still editable), and the save is the edit form's save
 * (saveEditedLeadRepAndEvent): the rep change, if any, through one compare-and-set call that moves the Sales Reps
 * list's Lead with it, then the event update with the manager levels.
 */
import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import { supabase } from "../../services/supabase";
import { updateEvent } from "../../services/events.service";
import {
  leadRepSyncNotice,
  syncLeadRepToSalesReps,
} from "../../services/salesCalendar.service";
import { saveEditedLeadRepAndEvent } from "../../utils/eventSelfService";
import { fillFromLeadChain } from "../../utils/repReportingChain";
import { eventKeys } from "../../hooks/useEvents";
import type { Event } from "../../types/event.types";
import SalesHierarchyFields, {
  type HierarchyChain,
  type SalesPositionRow,
} from "./SalesHierarchyFields";
import SalesHierarchyChainNote from "./SalesHierarchyChainNote";

interface Props {
  event: Event;
  onClose: () => void;
}

const EditSalesHierarchyDialog: React.FC<Props> = ({ event, onClose }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const loaded = useRef({
    current: {
      repId: event.sales_position_id ?? null,
      isSelfService: !!event.is_self_service,
    },
  });
  const [chain, setChain] = useState<HierarchyChain>({
    repId: event.is_self_service ? null : (event.sales_position_id ?? null),
    tmId: event.territory_manager_position_id ?? null,
    rmId: event.regional_manager_position_id ?? null,
    sdId: event.sales_director_position_id ?? null,
  });
  const [isSelfService, setIsSelfService] = useState(!!event.is_self_service);
  const [saving, setSaving] = useState(false);

  // Same rows and key as the create wizard's Team step.
  const { data: salesPositions = [], isLoading } = useQuery<SalesPositionRow[]>(
    {
      queryKey: ["sales-positions-active", "event-form-select"],
      queryFn: async () => {
        const { data } = await supabase
          .from("sales_positions")
          .select("id, title, assigned_name, position_type, parent_position_id")
          .eq("is_active", true)
          .order("assigned_name", { ascending: true, nullsFirst: false });
        return (data as SalesPositionRow[]) || [];
      },
    },
  );

  const save = async () => {
    setSaving(true);
    try {
      const leadResult = await saveEditedLeadRepAndEvent(
        loaded.current,
        { repId: chain.repId, isSelfService },
        {
          territory_manager_position_id: chain.tmId || null,
          regional_manager_position_id: chain.rmId || null,
          sales_director_position_id: chain.sdId || null,
        },
        (next, expected) => syncLeadRepToSalesReps(event.id, next, expected),
        (updates) =>
          updateEvent(
            event.id,
            updates as unknown as Parameters<typeof updateEvent>[1],
          ),
      );
      const notice = leadResult ? leadRepSyncNotice(leadResult) : null;
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(event.id) });
      queryClient.invalidateQueries({
        queryKey: ["event-sales-assignments", event.id],
      });
      queryClient.invalidateQueries({ queryKey: ["event-sales-hierarchy"] });
      if (leadResult?.error) {
        showToast(
          "warning",
          notice?.message ??
            "The managers were saved; the rep was not changed.",
        );
      } else {
        showToast("success", "Sales hierarchy updated");
        if (notice) {
          showToast(notice.type, notice.message);
        }
      }
      onClose();
    } catch (e) {
      showToast(
        "error",
        `The sales hierarchy was not saved: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title="Edit sales hierarchy"
      size="lg"
    >
      <div className="space-y-4" data-testid="edit-sales-hierarchy">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading sales positions…</p>
        ) : (
          <SalesHierarchyFields
            salesPositions={salesPositions}
            hierarchyChain={chain}
            isSelfService={isSelfService}
            onChange={(next, selfService) => {
              // A new Lead/Event Rep refills the manager levels from that rep's reporting chain (GS-06, as in the
              // create wizard); the fields stay editable.
              const leadChanged = !!next.repId && next.repId !== chain.repId;
              setChain(
                leadChanged
                  ? {
                      ...next,
                      ...fillFromLeadChain(
                        salesPositions,
                        next.repId,
                        next,
                        null,
                      ).levels,
                    }
                  : next,
              );
              setIsSelfService(selfService);
            }}
            note={
              <SalesHierarchyChainNote
                salesPositions={salesPositions}
                leadId={isSelfService ? null : chain.repId}
                current={chain}
                eventId={event.id}
                onUseLeadChain={(levels) =>
                  setChain((prev) => ({ ...prev, ...levels }))
                }
              />
            }
          />
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={saving || isLoading}
            isLoading={saving}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditSalesHierarchyDialog;
